import type { FC, ReactNode } from 'react'
import type { Office } from '@/types'

import { useClock } from '@/hooks/use-clock'
import { PointCloud } from '@/components/generals/point-cloud'

interface OfficeClockProps {
    office: Office
    description: string
    building: string
}

/**
 * One office panel: a small rotating point cloud of the building, the live local time, the address,
 * the phone number and a short note on who works there.
 */
export const OfficeClock: FC<OfficeClockProps> = ({ office, description, building }): ReactNode => {
    const clock = useClock(office.timeZone)
    const shape = office.code === 'LIS' ? 'office-lis' : 'office-sto'

    return (
        <article className='@container flex flex-col border border-hairline' aria-labelledby={`office-${office.code}`}>
            <PointCloud
                shape={shape}
                label={`Point cloud of the ${office.city} office: ${building}`}
                budget='small'
                rotate='always'
                rotateSpeed={6}
                hoverBoost={3}
                effects={false}
                frame
                grid
                className='aspect-[16/10] w-full border-b border-hairline'
            >
                <div className='pointer-events-none absolute inset-0 z-[2]' aria-hidden='true'>
                    <p className='hud absolute left-4 top-4 text-ink'>
                        {office.code} / {building}
                    </p>

                    <p className='hud absolute bottom-4 left-4 text-mute'>
                        {office.coords}
                    </p>
                </div>
            </PointCloud>

            <div className='grid flex-1 gap-8 p-5 @xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] desk:p-8'>
                <div>
                    <h3 id={`office-${office.code}`} className='hud text-ink'>
                        {office.city}
                    </h3>

                    <p className='mt-3 font-mono text-[40px] leading-none tracking-[0.02em] tabular-nums text-ink desk:text-[46px]' suppressHydrationWarning>
                        <span className='sr-only'>
                            Local time in {office.city}:&nbsp;
                        </span>

                        {clock.text}
                    </p>

                    <p className='hud mt-3 text-mute' suppressHydrationWarning>
                        {clock.offset || 'Local time'}
                    </p>

                    <p className='mt-6 text-[14px] leading-[1.6] text-ink/75'>
                        {description}
                    </p>
                </div>

                <dl className='self-start border-t border-hairline'>
                    <div className='border-b border-hairline py-3'>
                        <dt className='hud text-mute'>
                            Address
                        </dt>

                        <dd className='mt-1 text-[14px] leading-[1.55] text-ink'>
                            <address className='not-italic'>
                                {office.address.map(line => (
                                    <span key={line} className='block'>
                                        {line}
                                    </span>
                                ))}
                            </address>
                        </dd>
                    </div>

                    <div className='border-b border-hairline py-3'>
                        <dt className='hud text-mute'>
                            Phone
                        </dt>

                        <dd className='mt-1'>
                            <a href={office.phoneHref} className='text-[14px] text-ink underline decoration-hairline underline-offset-4 transition-colors duration-300 hover:decoration-ink'>
                                {office.phone}
                            </a>
                        </dd>
                    </div>
                </dl>
            </div>
        </article>
    )
}
